import { normalizeLabelCode } from '@barso/shared';
import type { CatalogItem } from './types';

/**
 * Suchindex über den Artikelstand dieser Kasse (U17b): ein Feld fester Einträge im Arbeitsspeicher, vorberechnet für die
 * Suche (`catalogSearch.ts`). Er entsteht einmal im Leerlauf aus dem lokalen Speicher und folgt danach den Seiten des
 * Abzugs, ohne neu zu laden. Es gibt keinen neuen IndexedDB-Index. Verkäufernamen kennt der Stand nicht (Datenschutz): Es
 * wird nur nach Nummern, Codes, Bezeichnung und Größe gesucht. Suchbegriffe verlassen nie das Gerät.
 */

export interface IndexEntry {
  id: string;
  /** BARSO-Code, 10 Ziffern. */
  code: string;
  /** Fremder Code (kanonisch geschrieben) oder `null`. */
  altCode: string | null;
  /** Ziffern des fremden Codes ohne Trennzeichen (leer, wenn keiner). */
  altDigits: string;
  sellerNumber: number;
  itemNo: number | null;
  priceCents: number;
  status: string;
  deleted: boolean;
  title: string;
  size: string | null;
  /** Bezeichnung und Größe, klein, ohne Akzente, `ß` als `ss`, Leerzeichen vereinheitlicht. */
  norm: string;
}

/** Klein geschrieben, über NFKD ohne Akzente, `ß → ss`, Leerraum vereinheitlicht („Mütze“ und „muetze“ sind verschieden, „Mütze“ und „Mutze“ gleich). */
export function normalizeText(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ')
    .trim();
}

export function toEntry(item: CatalogItem): IndexEntry {
  const altCode = item.altCode ?? null;
  return {
    id: item.id,
    code: item.code,
    altCode,
    altDigits: altCode === null ? '' : normalizeLabelCode(altCode).replace(/\D/g, ''),
    sellerNumber: item.sellerNumber,
    itemNo: item.itemNo ?? null,
    priceCents: item.priceCents,
    status: item.status,
    deleted: item.deleted,
    title: item.title,
    size: item.size,
    norm: normalizeText(item.size === null ? item.title : `${item.title} ${item.size}`),
  };
}

/** Quelle der Artikel (der lokale Speicher der Kasse). */
export interface ItemSource {
  allItems(): Promise<CatalogItem[]>;
}

/** Führt `task` im Leerlauf aus (`requestIdleCallback`, sonst kurz verzögert), damit das Kassieren nie wartet. */
export function whenIdle(task: () => void): void {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(task, { timeout: 2_000 });
  } else {
    setTimeout(task, 50);
  }
}

export class CatalogIndex {
  private readonly map = new Map<string, IndexEntry>();
  /** Artikel, die schon über den Abzug oder lokal kamen: ein späteres Laden aus dem Speicher überschreibt sie nicht mit älterem Stand. */
  private readonly touched = new Set<string>();
  private cache: IndexEntry[] | null = [];
  private ready = false;
  private loading: Promise<void> | null = null;
  private version = 0;
  private readonly listeners = new Set<() => void>();

  /** Der Index ist vollständig aus dem Speicher geladen. */
  get loaded(): boolean {
    return this.ready;
  }

  get size(): number {
    return this.map.size;
  }

  /** Zählt bei jeder Änderung hoch (für `useSyncExternalStore`). */
  get stamp(): number {
    return this.version;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private changed(): void {
    this.cache = null;
    this.version += 1;
    for (const listener of this.listeners) listener();
  }

  /** Alle Einträge als Feld (wird nur nach einer Änderung neu gebaut). */
  entries(): readonly IndexEntry[] {
    this.cache ??= [...this.map.values()];
    return this.cache;
  }

  /** Übernimmt eine Seite des Abzugs (auch Löschvermerke): ersetzt die Einträge über die Kennung. */
  apply(items: readonly CatalogItem[]): void {
    if (items.length === 0) return;
    for (const item of items) {
      this.map.set(item.id, toEntry(item));
      this.touched.add(item.id);
    }
    this.changed();
  }

  /** Lokale Verkäufe und Stornos ändern nur den Status; die Anzeige darf sonst kurz veraltet sein. */
  setStatus(ids: readonly string[], status: string): void {
    let any = false;
    for (const id of ids) {
      const entry = this.map.get(id);
      if (entry === undefined || entry.status === status) continue;
      this.map.set(id, { ...entry, status });
      this.touched.add(id);
      any = true;
    }
    if (any) this.changed();
  }

  /** Lädt den Stand aus dem Speicher (einmal; ein zweiter Aufruf wartet auf den ersten). */
  load(source: ItemSource): Promise<void> {
    this.loading ??= (async () => {
      const items = await source.allItems();
      for (const item of items) {
        if (!this.touched.has(item.id)) this.map.set(item.id, toEntry(item));
      }
      this.ready = true;
      this.changed();
    })();
    return this.loading;
  }

  /** Lädt im Leerlauf (Kasse öffnen): Fehler beim Laden lassen die Suche leer, das Kassieren bleibt unberührt. */
  loadWhenIdle(source: ItemSource): void {
    whenIdle(() => {
      this.load(source).catch(() => {
        this.loading = null;
      });
    });
  }
}
